import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bell, X, CreditCard, Truck, PackageCheck, AlertTriangle, Clock, Star, ShieldAlert,
  CheckCircle2, XCircle, Package, Undo2, Heart, ChevronRight, Inbox, CheckCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { avatarBus } from "@/components/avatar/HitAvatar";

/**
 * v10 Fase 2 — campanita con LISTA INDIVIDUAL de notificaciones.
 *
 * Origen de datos:
 *  · `/notifications/user-list` → docs de `db.user_notifications` (nuevos
 *    eventos de v10: pub_approved, order_paid, order_shipped, etc.). Cada
 *    doc tiene `link` = deep-link al ítem exacto.
 *  · `/notifications/counts` → contadores agregados legacy (por comprador /
 *    vendedor / otros) que quedan al final como accesos rápidos al dashboard.
 *
 * Badge total = unread individuales + counts.total agregado.
 *
 * Móvil (<768px): panel full-width fixed abajo del header, texto sin cortar,
 * touch target ≥56px. Desktop: dropdown compacto anclado a la campanita.
 */

const TYPE_ICON = {
  pub_approved: { Icon: CheckCircle2, color: "text-emerald-400" },
  pub_rejected: { Icon: XCircle, color: "text-red-400" },
  order_paid: { Icon: CreditCard, color: "text-emerald-400" },
  order_new_sale: { Icon: CreditCard, color: "text-[#EF4444]" },
  order_shipped: { Icon: Truck, color: "text-blue-400" },
  order_delivered: { Icon: PackageCheck, color: "text-emerald-400" },
  order_released: { Icon: CheckCircle2, color: "text-emerald-400" },
  order_refunded: { Icon: Undo2, color: "text-amber-400" },
  dispute_opened: { Icon: AlertTriangle, color: "text-red-400" },
  wishlist_hit: { Icon: Heart, color: "text-pink-400" },
  ship_extension_requested: { Icon: Clock, color: "text-amber-400" },
  ship_extension_decided: { Icon: Clock, color: "text-neutral-300" },
};

function iconFor(type) {
  return TYPE_ICON[type] || { Icon: Inbox, color: "text-neutral-400" };
}

// Avisos viejos guardaban "/orders/<id>" (ruta inexistente) o venían sin link.
function linkFor(n) {
  const link = String(n?.link || "");
  if (link.startsWith("/orders/")) return link.replace(/^\/orders\//, "/order/");
  if (link && link !== "/catalog") return link;
  if (n?.order_id) return `/order/${n.order_id}`;
  if (n?.type === "wishlist_hit" && n.card_id) return `/card/${n.card_id}/${n.language || "es"}/${n.variant || "normal"}`;
  return link || "/";
}

function timeAgo(iso) {
  if (!iso) return "";
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return "ahora";
  const m = Math.floor(s / 60);
  if (m < 60) return `hace ${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `hace ${d}d`;
  return new Date(iso).toLocaleDateString();
}

export function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("general");
  const boxRef = useRef(null);
  const panelRef = useRef(null);
  const nav = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);

  const load = async () => {
    try {
      const [rItems, rCounts] = await Promise.allSettled([
        api.get("/notifications/user-list", { params: { limit: 30 } }),
        api.get("/notifications/counts"),
      ]);
      if (rItems.status === "fulfilled") setItems(Array.isArray(rItems.value.data) ? rItems.value.data : []);
      if (rCounts.status === "fulfilled") setCounts(rCounts.value.data || {});
    } catch { /* silencioso — la campanita no molesta si la red falla */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  // Cerrar por click fuera / Escape (solo desktop — en móvil hay backdrop).
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (boxRef.current?.contains(e.target) || panelRef.current?.contains(e.target) || e.target?.dataset?.notifBackdrop) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);
  const badgeTotal = unreadCount + (counts.total || 0);
  const bellRef = useRef(null);
  const prevTotal = useRef(null);
  // Notificación nueva: la campana se balancea y el avatar del encabezado salta hacia ella.
  useEffect(() => {
    const prev = prevTotal.current;
    prevTotal.current = badgeTotal;
    if (prev === null || badgeTotal <= prev) return;
    avatarBus.notify();
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!reduce) bellRef.current?.animate?.([{ transform: "rotate(0)" }, { transform: "rotate(18deg)" }, { transform: "rotate(-18deg)" }, { transform: "rotate(10deg)" }, { transform: "rotate(-6deg)" }, { transform: "rotate(0)" }],
      { duration: 800, delay: 250, easing: "ease-in-out" });
  }, [badgeTotal]);

  // Al tocar un aviso: navega al link exacto + marca leído (optimistic UI).
  // Fallback: si un aviso viejo no trae `link`, cae al home — nunca falla.
  const openItem = async (n) => {
    setOpen(false);
    // Optimistic mark-read en la lista local
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    // API mark-read (best-effort, no bloqueamos la navegación)
    api.post(`/notifications/user-mark-one-read/${n.id}`).catch(() => {});
    nav(linkFor(n));
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/user-mark-read");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    } catch { /* noop */ }
  };

  const goShortcut = (to) => { setOpen(false); nav(to); };

  const BUYER_ROWS = [
    { key: "orders_to_pay", icon: CreditCard, label: "Pedidos por pagar", to: "/buyer" },
    { key: "orders_to_receive", icon: PackageCheck, label: "Envíos por recibir", to: "/buyer" },
    { key: "disputes_buyer", icon: AlertTriangle, label: "Disputas abiertas", to: "/buyer" },
  ];
  const SELLER_ROWS = [
    { key: "orders_to_ship", icon: Truck, label: "Pedidos por enviar", to: "/seller?tab=orders" },
    { key: "orders_awaiting_deliver", icon: Clock, label: "En camino (esperando entrega)", to: "/seller?tab=orders" },
    { key: "pubs_in_review", icon: Clock, label: "Publicaciones en revisión", to: "/seller?tab=catalog" },
    { key: "disputes_seller", icon: AlertTriangle, label: "Disputas abiertas", to: "/seller?tab=orders" },
  ];
  const BOTH_ROWS = [
    { key: "security_tickets", icon: ShieldAlert, label: "Tickets de seguridad", to: "/setup-2fa" },
  ];
  const RATING_ROWS = [
    { key: "ratings_pending", icon: Star, label: "Calificaciones pendientes", to: "/calificaciones" },
  ];
  const visible = (rows) => rows.filter((r) => (counts[r.key] || 0) > 0);
  const buyer = visible(BUYER_ROWS);
  const seller = visible(SELLER_ROWS);
  const both = visible(BOTH_ROWS);
  const ratings = visible(RATING_ROWS);
  const isOrder = (n) => /^(order_|dispute_|ship_)/.test(n.type || "") || String(n.link || "").startsWith("/order");
  const orderItems = items.filter(isOrder);
  const shownItems = tab === "orders" ? orderItems : items;
  const unreadGeneral = unreadCount;
  const unreadOrders = orderItems.filter((n) => !n.read).length;
  const tabShortcuts = tab === "orders" ? buyer.length + seller.length + ratings.length > 0 : both.length > 0;
  const TabBtn = ({ k, label, n }) => (
    <button type="button" onClick={() => setTab(k)} data-testid={`notifications-tab-${k}`}
      className={`flex-1 py-2.5 text-xs font-semibold border-b-2 transition-colors ${tab === k ? "border-[#EF4444] text-white" : "border-transparent text-neutral-500 hover:text-white"}`}>
      {label}{n > 0 && <span className="ml-1.5 bg-[#EF4444] text-white text-[10px] rounded-full px-1.5 py-0.5">{n > 99 ? "99+" : n}</span>}
    </button>
  );

  const panelBody = (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
        <div className="text-sm font-semibold text-white flex items-center gap-2">
          <Bell size={14} /> Notificaciones{unreadCount > 0 ? ` (${unreadCount})` : ""}
        </div>
        <div className="flex items-center gap-1">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              data-testid="notifications-mark-all"
              className="text-[11px] text-neutral-400 hover:text-white flex items-center gap-1 px-2 py-1 rounded hover:bg-[#171717]"
            >
              <CheckCheck size={13} /> Marcar todas
            </button>
          )}
          <button type="button" onClick={() => setOpen(false)} data-testid="notifications-close" className="text-neutral-500 hover:text-white p-1">
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="flex border-b border-[#262626]" role="tablist">
        <TabBtn k="general" label="Generales" n={unreadGeneral + (counts.security_tickets || 0)} />
        <TabBtn k="orders" label="Pedidos" n={unreadOrders + buyer.concat(seller, ratings).reduce((s, r) => s + (counts[r.key] || 0), 0)} />
      </div>

      <div className="overflow-y-auto flex-1">
        {shownItems.length === 0 && !tabShortcuts && (
          <div className="p-8 text-center text-sm text-neutral-500" data-testid="notifications-empty">
            <Inbox size={28} className="mx-auto mb-2 text-neutral-700" />
            {tab === "orders" ? "No tienes avisos de pedidos." : "Nada pendiente. Todo al día."}
          </div>
        )}

        {tab === "orders" && buyer.length + seller.length + ratings.length > 0 && (
          <div>
            <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-neutral-500 bg-[#0A0A0A] border-b border-[#262626]">Pendientes</div>
            {buyer.length > 0 && <ShortcutSection title="Como comprador" rows={buyer} counts={counts} onClick={goShortcut} testidPrefix="notif-buyer" />}
            {seller.length > 0 && <ShortcutSection title="Como vendedor" rows={seller} counts={counts} onClick={goShortcut} testidPrefix="notif-seller" />}
            {ratings.length > 0 && <ShortcutSection title="Calificaciones" rows={ratings} counts={counts} onClick={goShortcut} testidPrefix="notif-other" />}
          </div>
        )}

        {shownItems.length > 0 && (
          <div>
            <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-neutral-500 bg-[#0A0A0A] border-y border-[#262626]">
              {tab === "orders" ? "Actividad de pedidos" : "Actividad reciente"}
            </div>
            {shownItems.map((n) => {
              const { Icon, color } = iconFor(n.type);
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openItem(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full min-h-[64px] flex items-start gap-3 px-4 py-3 hover:bg-[#171717] transition-colors border-b border-[#171717] last:border-b-0 text-left ${!n.read ? "bg-[#EF4444]/[0.04]" : ""}`}
                >
                  <div className="shrink-0 pt-0.5">
                    <Icon size={18} className={color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white line-clamp-1">{n.title || "Aviso"}</span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-[#3b82f6] shrink-0" data-testid={`notif-unread-dot-${n.id}`} />}
                    </div>
                    <div className="text-[12px] text-neutral-400 mt-0.5 whitespace-normal break-words line-clamp-2">
                      {n.message || ""}
                    </div>
                    <div className="text-[10px] text-neutral-600 mt-1">{timeAgo(n.created_at)}</div>
                  </div>
                  <ChevronRight size={14} className="text-neutral-600 shrink-0 mt-1" />
                </button>
              );
            })}
          </div>
        )}

        {tab === "general" && both.length > 0 && (
          <div>
            <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-neutral-500 bg-[#0A0A0A] border-y border-[#262626]">
              Accesos rápidos
            </div>
            <ShortcutSection title="Otros" rows={both} counts={counts} onClick={goShortcut} testidPrefix="notif-other" />
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="relative" ref={boxRef}>
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); load(); }}
        data-testid="header-notifications"
        aria-label="Notificaciones"
        aria-expanded={open}
        className={`relative w-10 h-10 grid place-items-center rounded-full transition-colors ${open ? "bg-[#EF4444]/15 text-[#EF4444]" : "text-neutral-300 hover:text-white"}`}
      >
        <span ref={bellRef} className="inline-flex origin-top" data-testid="notifications-bell-icon"><Bell size={20} strokeWidth={2} /></span>
        {badgeTotal > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-[2px] -right-[4px] bg-[#EF4444] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center leading-none"
          >
            {badgeTotal > 99 ? "99+" : badgeTotal}
          </span>
        )}
      </button>

      {open && createPortal(
        <>
          {/* Backdrop móvil bajo el encabezado: el header sigue siempre tocable. */}
          <div
            data-notif-backdrop="1"
            data-testid="notifications-backdrop"
            className="md:hidden fixed inset-x-0 top-16 bottom-0 bg-black/40 z-[45]"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={panelRef}
            data-testid="notifications-panel"
            className="fixed z-[46] top-[68px] left-2 right-2 max-h-[min(70svh,calc(100svh-84px))] md:left-auto md:right-[max(1rem,calc((100vw-80rem)/2+1rem))] md:w-[380px] md:max-h-[80vh] md:rounded-md md:shadow-2xl bg-[#111] border border-[#262626] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {panelBody}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

function ShortcutSection({ title, rows, counts, onClick, testidPrefix }) {
  return (
    <div>
      <div className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-neutral-600">{title}</div>
      {rows.map((r) => (
        <button
          key={r.key}
          type="button"
          onClick={() => onClick(r.to)}
          data-testid={`${testidPrefix}-${r.key}`}
          className="w-full min-h-[52px] flex items-center gap-3 px-4 py-2.5 hover:bg-[#171717] transition-colors border-b border-[#171717] last:border-b-0 text-left"
        >
          <r.icon size={16} className="text-[#EF4444] shrink-0" />
          <span className="flex-1 text-sm text-neutral-200 line-clamp-1">{r.label}</span>
          <span className="bg-[#EF4444] text-white text-[10px] font-bold min-w-[20px] h-[20px] px-1.5 rounded-full flex items-center justify-center leading-none">
            {counts[r.key] > 99 ? "99+" : counts[r.key]}
          </span>
        </button>
      ))}
    </div>
  );
}

export default NotificationsBell;
